import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Check, X, Clock } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { de } from 'date-fns/locale';
import axios from 'axios';
import { Calendar } from './ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AvailabilityCalendar = () => {
  const [selectedDate, setSelectedDate] = useState();
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  const fetchAvailability = async (date) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bookings/availability/${date}`);
      setAvailability(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Verfügbarkeit:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="availability" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent">
              Verfügbarkeitskalender
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            Prüfen Sie freie Termine oder buchen Sie spontan, wenn noch Termine verfügbar sind
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calendar Selection */}
          <Card className="shadow-xl border-2 border-gray-200">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-orange-500 text-white">
              <CardTitle className="flex items-center space-x-2">
                <CalendarIcon className="h-6 w-6" />
                <span>Datum auswählen</span>
              </CardTitle>
              <CardDescription className="text-blue-50">
                Wählen Sie ein Datum, um verfügbare Zeitslots zu sehen
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 flex justify-center">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-14 text-lg"
                  >
                    <CalendarIcon className="mr-2 h-5 w-5" />
                    {selectedDate ? format(selectedDate, 'PPP', { locale: de }) : 'Datum auswählen'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => date < new Date()}
                    locale={de}
                  />
                </PopoverContent>
              </Popover>
            </CardContent>
          </Card>

          {/* Availability Display */}
          <Card className="shadow-xl border-2 border-gray-200">
            <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-6 w-6" />
                <span>Verfügbare Zeiten</span>
              </CardTitle>
              <CardDescription className="text-orange-50">
                {selectedDate 
                  ? format(selectedDate, 'EEEE, dd. MMMM yyyy', { locale: de })
                  : 'Wählen Sie ein Datum aus'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {!selectedDate && (
                <div className="text-center py-12 text-gray-500">
                  <CalendarIcon className="h-16 w-16 mx-auto mb-4 opacity-20" />
                  <p>Bitte wählen Sie ein Datum aus, um verfügbare Zeitslots zu sehen</p>
                </div>
              )}

              {selectedDate && loading && (
                <div className="text-center py-12 text-gray-500">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p>Lade Verfügbarkeit...</p>
                </div>
              )}

              {selectedDate && !loading && availability && (
                <div>
                  {availability.availableSlots.length === 0 ? (
                    <div className="text-center py-8 bg-red-50 rounded-xl border-2 border-red-200">
                      <X className="h-12 w-12 mx-auto text-red-600 mb-3" />
                      <p className="text-red-800 font-semibold">Keine freien Termine verfügbar</p>
                      <p className="text-sm text-red-600 mt-2">
                        Bitte wählen Sie ein anderes Datum
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <Check className="h-5 w-5 text-green-600 mr-2" />
                          Verfügbare Zeitslots ({availability.availableSlots.length})
                        </h4>
                        <div className="grid grid-cols-3 gap-3">
                          {availability.availableSlots.map((slot) => (
                            <div
                              key={slot}
                              className="bg-green-50 border-2 border-green-300 rounded-lg p-3 text-center font-semibold text-green-700 hover:bg-green-100 transition-colors cursor-pointer"
                            >
                              {slot} Uhr
                            </div>
                          ))}
                        </div>
                      </div>

                      {availability.bookedSlots.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                            <X className="h-5 w-5 text-red-600 mr-2" />
                            Bereits belegt ({availability.bookedSlots.length})
                          </h4>
                          <div className="grid grid-cols-3 gap-3">
                            {availability.bookedSlots.map((slot) => (
                              <div
                                key={slot}
                                className="bg-red-50 border-2 border-red-300 rounded-lg p-3 text-center font-semibold text-red-700 opacity-60"
                              >
                                {slot} Uhr
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {availability.blockedSlots && availability.blockedSlots.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                            <X className="h-5 w-5 text-orange-600 mr-2" />
                            Nicht verfügbar ({availability.blockedSlots.length})
                          </h4>
                          <div className="grid grid-cols-3 gap-3">
                            {availability.blockedSlots.map((slot) => (
                              <div
                                key={`blocked-${slot}`}
                                className="bg-orange-50 border-2 border-orange-300 rounded-lg p-3 text-center font-semibold text-orange-700 opacity-60"
                              >
                                {slot} Uhr
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>Hinweis:</strong> Termine können spontan gebucht werden, solange Zeitslots verfügbar sind. 
                      Für eine sichere Buchung empfehlen wir jedoch eine Vorlaufzeit von 7 Tagen.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Important Notes */}
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6">
            <h3 className="font-bold text-lg text-orange-900 mb-3">Langstrecken-Transporte</h3>
            <ul className="space-y-2 text-sm text-orange-800">
              <li>• Buchung 1 Woche im Voraus erforderlich</li>
              <li>• 50% Anzahlung bei Buchung</li>
              <li>• Keine Erstattung bei Absage durch Auftraggeber</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
            <h3 className="font-bold text-lg text-blue-900 mb-3">Spontanbuchungen</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li>• Nach Verfügbarkeit möglich</li>
              <li>• Prüfen Sie die freien Zeitslots im Kalender</li>
              <li>• Telefonische Bestätigung empfohlen</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AvailabilityCalendar;
